import React, { useState, useEffect, useMemo } from 'react';
import NavBar from '../components/NavBar.jsx';
import ModalClose from '../components/ModalClose.jsx';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import api from '../services/api.js';
import { useAuth } from '../App.jsx';

// Couleurs par thème — alignées sur Curiosaurus.jsx
const THEME_META = {
  espace: { icon: '🚀', color: '#4A90D9', label: 'Espace' },
  geographie: { icon: '🌍', color: '#2e7d32', label: 'Géographie' },
  mythologie: { icon: '⚡', color: '#9c27b0', label: 'Mythologie' },
  'corps-humain': { icon: '🫀', color: '#e53935', label: 'Corps humain' },
  histoire: { icon: '⏳', color: '#F5A623', label: 'Histoire' },
  animaux: { icon: '🦁', color: '#00ACC1', label: 'Animaux' },
  personnages: { icon: '👤', color: '#E91E90', label: 'Personnages' },
  sport: { icon: '⚽', color: '#FF6B35', label: 'Sport' },
  inventions: { icon: '💡', color: '#FFD93D', label: 'Inventions' },
};

const DEFAULT_META = { icon: '📖', color: '#888', label: 'Autre' };

function metaFor(slug) {
  return THEME_META[slug] || DEFAULT_META;
}

export default function Cards() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCard, setSelectedCard] = useState(null);

  // Préfiltre depuis ?theme=slug dans l'URL (utilisé quand on arrive depuis Curiosaurus)
  const initialFilter = useMemo(() => {
    const params = new URLSearchParams(location.search);
    const t = params.get('theme');
    if (t && THEME_META[t]) return t;
    return 'all';
  }, [location.search]);

  const [filter, setFilter] = useState(initialFilter);

  useEffect(() => {
    api.getCards()
      .then(c => setCards(c))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  // Compter les fiches par thème
  const counts = useMemo(() => {
    const c = {};
    for (const uc of cards) {
      const slug = uc.fact_card.theme_slug || 'autre';
      c[slug] = (c[slug] || 0) + 1;
    }
    return c;
  }, [cards]);

  // Ordre d'affichage des chips : les thèmes présents en premier, dans l'ordre de THEME_META
  const themesPresents = useMemo(() => {
    return Object.keys(THEME_META).filter(slug => counts[slug] > 0);
  }, [counts]);

  // Filtrer les cartes
  const filtered = useMemo(() => {
    if (filter === 'all') return cards;
    return cards.filter(uc => uc.fact_card.theme_slug === filter);
  }, [cards, filter]);

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
      <NavBar />

      <div className="container">
        <button className="back-btn" onClick={() => navigate('/')}>
          ← Retour
        </button>

        <div className="section-title">
          📚 Mon encyclopédie ({cards.length} fiche{cards.length > 1 ? 's' : ''})
        </div>

        {loading ? (
          <div style={{ textAlign: 'center', padding: 40 }}>
            <div className="loading-rocket">📚</div>
            <p style={{ color: 'var(--text-dim)', marginTop: 12 }}>Chargement...</p>
          </div>
        ) : cards.length === 0 ? (
          <div className="cards-empty">
            <div className="emoji">🔭</div>
            <h3>Ton encyclopédie est vide !</h3>
            <p style={{ marginTop: 8 }}>Réponds à des quiz pour collecter des fiches "Le savais-tu ?"</p>
            <button
              className="btn btn-primary"
              style={{ maxWidth: 300, margin: '24px auto 0' }}
              onClick={() => navigate('/')}
            >
              🚀 Commencer un quiz
            </button>
          </div>
        ) : (
          <>
            {/* Chips de filtres par thème */}
            <div className="cards-filter-chips">
              <button
                className={`chip ${filter === 'all' ? 'chip-active' : ''}`}
                onClick={() => setFilter('all')}
              >
                <span className="chip-icon">📚</span>
                <span>Tout</span>
                <span className="chip-count">{cards.length}</span>
              </button>
              {themesPresents.map(slug => {
                const meta = metaFor(slug);
                const isActive = filter === slug;
                return (
                  <button
                    key={slug}
                    className={`chip ${isActive ? 'chip-active' : ''}`}
                    onClick={() => setFilter(slug)}
                    style={isActive ? { borderColor: meta.color, color: meta.color } : undefined}
                  >
                    <span className="chip-icon">{meta.icon}</span>
                    <span>{meta.label}</span>
                    <span className="chip-count">{counts[slug]}</span>
                  </button>
                );
              })}
            </div>

            {/* Grille */}
            <div className="cards-grid">
              {filtered.map((uc, i) => {
                const meta = metaFor(uc.fact_card.theme_slug);
                return (
                  <motion.div
                    key={uc.id}
                    className="card-item"
                    onClick={() => setSelectedCard(uc.fact_card)}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: Math.min(i, 20) * 0.03 }}
                    style={{ borderTop: `3px solid ${meta.color}` }}
                  >
                    <div className="card-theme-badge" style={{ color: meta.color }}>
                      <span>{meta.icon}</span>
                      <span style={{ fontSize: 11, fontWeight: 600 }}>{meta.label}</span>
                    </div>
                    <h3>💡 {uc.fact_card.title}</h3>
                    <p>
                      {uc.fact_card.content.length > 120
                        ? uc.fact_card.content.slice(0, 120) + '...'
                        : uc.fact_card.content}
                    </p>
                    {uc.fact_card.tags.length > 0 && (
                      <div className="card-tags">
                        {uc.fact_card.tags.map(tag => (
                          <span key={tag} className="card-tag">{tag}</span>
                        ))}
                      </div>
                    )}
                  </motion.div>
                );
              })}
            </div>

            {filtered.length === 0 && (
              <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-dim)' }}>
                Aucune fiche pour ce thème pour l'instant.
              </div>
            )}
          </>
        )}
      </div>

      {/* Card detail modal */}
      <AnimatePresence>
        {selectedCard && (
          <motion.div
            className="modal-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setSelectedCard(null)}
          >
            <motion.div
              className="modal"
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={e => e.stopPropagation()}
            >
              <ModalClose onClick={() => setSelectedCard(null)} />
              <div className="fact-card-badge">💡 Le savais-tu ?</div>
              {selectedCard.theme_slug && (
                <div
                  className="card-theme-badge"
                  style={{ color: metaFor(selectedCard.theme_slug).color, marginBottom: 8 }}
                >
                  <span>{metaFor(selectedCard.theme_slug).icon}</span>
                  <span style={{ fontSize: 12, fontWeight: 600 }}>
                    {metaFor(selectedCard.theme_slug).label}
                  </span>
                </div>
              )}
              <h3>{selectedCard.title}</h3>
              <p>{selectedCard.content}</p>
              {selectedCard.tags.length > 0 && (
                <div className="card-tags" style={{ marginTop: 16 }}>
                  {selectedCard.tags.map(tag => (
                    <span key={tag} className="card-tag">{tag}</span>
                  ))}
                </div>
              )}
              <button
                className="btn btn-primary modal-close"
                onClick={() => setSelectedCard(null)}
              >
                Fermer
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}
